import './Profile.scss';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { AnimatePresence } from 'framer-motion';
import GraphemeSplitter from 'grapheme-splitter';
import { gsap } from 'gsap';

import { ThemeContext } from 'styled-components';
// Client
import { Description, Title, YatBadge, yatLevelFromRhythmScore, TitleProps } from './components';
import {
    ContentWrapper,
    LiquidIDPosition,
    ProfileWrapper,
    UsernameError,
    UsernameErrorTooltipText,
    UsernameErrorWrapper,
} from './styles';
import { EmojiId } from '@/client/common/structs/emoji/emoji';
import { ReactComponent as InfoIcon } from '@/assets/icons/info.svg';
// Store
import { useAppDispatch, useAppSelector, useGetUserFantasyWishlists } from '@/hooks';

import { setYatPageBio, setYatPageHeading, updateYatPageEditStates } from '@/store/slices/yat-page';
import { DESCRIPTION_MAX_LENGTH } from '@/store/slices/yat-page/yat-page';

// Components
import { useMediaQuery } from '@/providers';
import { PortalArea, Tooltip, usePortal, AttributeInput, YatShareModule } from '@/components';

import { MobileInputEditCard } from '@/components/Sections/Profile/MobileInputEditCard';
import { CharacterLimit } from '@/pages/Prototype/components/PlainTextEditor/components';
import { PROFILE_TITLE_ID_EDITABLE } from '@/pages/Prototype/components/Profile/components/Title/Title';
import { PROFILE_DESCRIPTION_ID_EDITABLE } from '@/pages/Prototype/components/Profile/components/Description/Description';

import { ONBOARDING_LID_EDIT_CLASS } from '@/components/OnboardingTutorial/styles';
import { useGetEmojiDetails } from '@/pages/Prototype/components/Profile/hooks';
import { addNotification } from '@/store/actions/notification';
import { USERNAME_MAX_CHAR, validateUsername } from '@/helpers';

const CHAR_LIMITS = {
    title: USERNAME_MAX_CHAR,
    description: DESCRIPTION_MAX_LENGTH,
};
const graphemeSplitter = new GraphemeSplitter();

export enum ProfileAttributeTypes {
    title = 'title',
    description = 'description',
}

export interface ProfileProps {
    children?: React.ReactNode;
    editMode?: boolean;
    onResize?: (screenWidth: number, profileWidth: number) => void;
    emojiID?: EmojiId;
    onInvalid?: (invalid: boolean) => void;
}

export function Profile({ children, editMode = false, onResize, onInvalid, emojiID }: ProfileProps) {
    const theme = useContext(ThemeContext);
    const { data: emojiDataResponse } = useGetEmojiDetails(emojiID);
    const userWishlists = useGetUserFantasyWishlists(!editMode); // might be a better way to check this
    const userHasASubmittedLineup = !!userWishlists?.length;
    const { desktop, tablet, usingMouse } = useMediaQuery();
    const description = useAppSelector((state) => state.yatPage.bio);
    const title = useAppSelector((state) => state.yatPage.heading);
    const liquidIDExpandedPreview = useAppSelector((state) => state.yatPage.liquidIDExpandedPreview);
    const user_id = useAppSelector((state) => state.yatPage.user_id);
    const editingLiquidID = useAppSelector((state) => state.yatPage.editStates.editingLiquidID);
    const editingTitle = useAppSelector((state) => state.yatPage.editStates.editingHeading);
    const editingDescription = useAppSelector((state) => state.yatPage.editStates.editingBio);
    const [username, setUsername] = useState(title);

    const { t } = useTranslation(['yat_page', 'fantasy_wishlist']);

    const [titleLength, setTitleLength] = useState(0);
    const [descriptionLength, setDescriptionLength] = useState(0);

    useEffect(() => {
        setDescriptionLength(description?.length || 0);
    }, [description]);

    useEffect(() => {
        setTitleLength(title?.length || 0);
    }, [title]);

    const liquidIdWrapper = useRef<HTMLDivElement>(null);
    const [protoRhythmScore, setCurrentRhythmScore] = useState<number>(0);
    const [protoGeneration, setCurrentGeneration] = useState<number>(0);
    const dispatch = useAppDispatch();

    const { setPortalArea } = usePortal();
    const profileWrapperRef = useRef<HTMLDivElement>(null);
    const headingRef = useRef<HTMLDivElement>(null);
    const bioRef = useRef<HTMLDivElement>(null);
    const badgeRef = useRef<HTMLDivElement>(null);

    const setEditingTitle = useCallback(
        (editing: boolean) =>
            dispatch(
                updateYatPageEditStates({
                    editingHeading: editing,
                }),
            ),
        [dispatch],
    );
    const setEditingDescription = useCallback(
        (editing: boolean) =>
            dispatch(
                updateYatPageEditStates({
                    editingBio: editing,
                }),
            ),
        [dispatch],
    );

    useEffect(() => {
        setPortalArea([PortalArea.Profile, profileWrapperRef]);
        return () => setPortalArea([PortalArea.Profile, null]);
    }, [setPortalArea]);

    useEffect(() => {
        if (emojiDataResponse) {
            if (emojiDataResponse.ok && emojiDataResponse.value.result) {
                setCurrentRhythmScore(emojiDataResponse.value.result.rhythm_score);
                setCurrentGeneration(emojiDataResponse.value.result.generation);
            }
        }
    }, [emojiDataResponse]);

    const handleTitleChange = useCallback((val?: string | null) => {
        setUsername(val || '');
        setTitleLength(val?.length || 0);
    }, []);

    const handleUpdateDescription: TitleProps['onUpdateValue'] = useCallback(
        (value) => {
            dispatch(setYatPageBio(value ?? ''));
        },
        [dispatch],
    );

    // TODO: All of these need to be removed before launch.
    // TODO: What is the difference between `emojiID` and `emojis`?
    const protoYat = useMemo(() => {
        return emojiID ? graphemeSplitter.splitGraphemes(emojiID) : [];
    }, [emojiID]);

    const yatLevel = useMemo(() => {
        return yatLevelFromRhythmScore(protoRhythmScore || 0);
    }, [protoRhythmScore]);

    // TODO: It might make sense to have a single component:
    // <YatBadgeCombo emojis={emojis} score={score} />
    const yatBadgeMarkup = useMemo(() => {
        const hideBadge = liquidIDExpandedPreview || (!tablet && editingLiquidID);
        return (
            <div ref={badgeRef} className="YatBadgeWrapper" style={{ visibility: hideBadge ? 'hidden' : 'visible' }}>
                <YatBadge motionEntrance>
                    <YatBadge.RhythmScore score={protoRhythmScore} generation={protoGeneration} />
                    <YatBadge.Pin emojis={protoYat} level={yatLevel} />
                </YatBadge>
            </div>
        );
    }, [protoRhythmScore, protoGeneration, protoYat, yatLevel, liquidIDExpandedPreview, editingLiquidID, tablet]);

    const usernameError = useMemo(() => {
        const messages = validateUsername(username || '');
        const message = messages.find((m) => m.length > 0);

        if (userHasASubmittedLineup && message) {
            return (
                <UsernameErrorWrapper>
                    <UsernameError>{t(`fantasy_wishlist:${message}`)}</UsernameError>
                    <Tooltip trigger={<InfoIcon />} placement="bottom" variant="secondary">
                        <UsernameErrorTooltipText>{t('fantasy_wishlist:username_error_info')}</UsernameErrorTooltipText>
                    </Tooltip>
                </UsernameErrorWrapper>
            );
        }
        return undefined;
    }, [t, userHasASubmittedLineup, username]);
    const handleUpdateTitle: TitleProps['onUpdateValue'] = useCallback(
        (value) => {
            if (userHasASubmittedLineup && !value?.length) {
                dispatch(
                    addNotification({
                        title: `Username can't be empty 👀`,
                        message: t('fantasy_wishlist:username_error_info'),
                        severity: 'info',
                        duration: 4000,
                    }),
                );
                return;
            }
            if (!usernameError) {
                dispatch(setYatPageHeading(value ?? ''));
            }
        },
        [userHasASubmittedLineup, usernameError, dispatch, t],
    );

    useEffect(() => {
        onInvalid?.(!!usernameError);
    }, [onInvalid, usernameError]);

    const liquidIdMarkup = desktop && !editingLiquidID && !liquidIDExpandedPreview && (
        <LiquidIDPosition
            className={ONBOARDING_LID_EDIT_CLASS}
            forwardedRef={liquidIdWrapper}
            $desktop
            editMode={editMode}
        />
    );
    const editingMarkup = !editingLiquidID && user_id && (
        <>
            {!liquidIDExpandedPreview && !editMode && !title ? null : (
                <div ref={headingRef}>
                    <Title
                        placeholder={t('placeholder_title')}
                        characterLimit={CHAR_LIMITS[ProfileAttributeTypes.title]}
                        value={title}
                        editMode={editMode}
                        editing={editingTitle}
                        onChange={handleTitleChange}
                        onEnableEditing={() => setEditingTitle(true)}
                        onDisableEditing={() => {
                            usingMouse ? setEditingTitle(false) : undefined;
                        }}
                        onUpdateValue={handleUpdateTitle}
                    />
                    {editMode && usernameError}
                    <MobileInputEditCard open={!!editingTitle} onClose={() => setEditingTitle(false)} forceBottom>
                        <AttributeInput
                            onClose={() => setEditingTitle(false)}
                            placeholder={t('placeholder_title')}
                            value={title}
                            onChange={handleTitleChange}
                            onUpdated={handleUpdateTitle}
                            characterLimit={CHAR_LIMITS[ProfileAttributeTypes.title]}
                        />
                    </MobileInputEditCard>
                </div>
            )}
            {!editMode && !description ? null : (
                <div ref={bioRef}>
                    <Description
                        placeholder={t('placeholder_description')}
                        value={description}
                        editMode={editMode}
                        onChange={(val) => setDescriptionLength(val?.length || 0)}
                        editing={editingDescription}
                        characterLimit={CHAR_LIMITS[ProfileAttributeTypes.description]}
                        onEnableEditing={() => setEditingDescription(true)}
                        onDisableEditing={() => {
                            usingMouse ? setEditingDescription(false) : undefined;
                        }}
                        onUpdateValue={handleUpdateDescription}
                    />
                    <MobileInputEditCard
                        open={!!editingDescription}
                        onClose={() => setEditingDescription(false)}
                        forceBottom
                    >
                        <AttributeInput
                            onClose={() => setEditingDescription(false)}
                            placeholder={t('placeholder_description')}
                            value={description}
                            onUpdated={handleUpdateDescription}
                            characterLimit={CHAR_LIMITS[ProfileAttributeTypes.description]}
                        />
                    </MobileInputEditCard>
                </div>
            )}
            {!editMode && tablet && (
                <div className="Yat_Profile-Share">
                    <YatShareModule emojiId={emojiID as string} soloButton={true} directionUp directionRight />
                </div>
            )}
            <AnimatePresence>
                {editingTitle ? (
                    <CharacterLimit
                        key="Profile-Title-CharacterLimit"
                        limit={CHAR_LIMITS[ProfileAttributeTypes.title]}
                        used={titleLength || 0}
                        contributors={[PROFILE_TITLE_ID_EDITABLE]}
                    />
                ) : null}

                {editingDescription ? (
                    <CharacterLimit
                        key="Profile-Description-CharacterLimit"
                        limit={CHAR_LIMITS[ProfileAttributeTypes.description]}
                        used={descriptionLength || 0}
                        contributors={[PROFILE_DESCRIPTION_ID_EDITABLE]}
                    />
                ) : null}
            </AnimatePresence>
        </>
    );

    const trailingMarkup = children ? <div className="Yat_Profile-Children">{children}</div> : null;

    const setBadgeTop = useCallback(
        (top?: number) => {
            if (!badgeRef.current) return;

            gsap.to(badgeRef.current, {
                y: top || '',
                duration: theme.transitions.speeds.normal,
                ease: 'power2.out',
            });
        },
        [theme],
    );

    const updateBadgePosition = useCallback(() => {
        if (!liquidIdWrapper.current) return;

        if (editingLiquidID && !desktop) {
            const positionMobileHeight = liquidIdWrapper.current.getBoundingClientRect().height;
            setBadgeTop(-positionMobileHeight);
        } else setBadgeTop();
    }, [desktop, editingLiquidID, setBadgeTop]);

    const handleResize = useCallback(() => {
        if (profileWrapperRef.current && profileWrapperRef.current.clientWidth && onResize) {
            onResize(window.innerWidth, profileWrapperRef.current.clientWidth);
        }

        updateBadgePosition();
    }, [onResize, updateBadgePosition]);

    useEffect(() => {
        updateBadgePosition();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [updateBadgePosition, handleResize]);

    return (
        <ProfileWrapper
            ref={profileWrapperRef}
            $isEdit={editMode}
            $editingLiquidID={editingLiquidID}
            $newNav
            $liquidIDExpanded={liquidIDExpandedPreview}
        >
            {!desktop && !editingLiquidID && !liquidIDExpandedPreview && (
                <LiquidIDPosition
                    forwardedRef={liquidIdWrapper}
                    $editingLiquidID={editingLiquidID}
                    editMode={editMode}
                />
            )}
            <ContentWrapper className="Yat_Profile-Content" $isEdit={editMode} $editingLiquidID={editingLiquidID}>
                <div className="Yat_Profile-Typography">
                    {liquidIdMarkup}
                    {user_id && yatBadgeMarkup}
                    {editingMarkup}
                </div>
            </ContentWrapper>
            {trailingMarkup}
        </ProfileWrapper>
    );
}
